import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Close when clicking outside the menu
   document.addEventListener(
  'click',
  (e) => {
    if (!(e.target instanceof Node)) return;

    // If click is inside this header-menu, ignore
    if (this.contains(e.target)) return;

    // If click is inside an open submenu/overflow panel, ignore
    const activePanel = this.querySelector('[ref="submenu[]"][data-active]') || this.overflowMenu;
    if (activePanel && activePanel.contains(e.target)) return;

    this.#deactivate();
  },
  { signal: this.#abortController.signal }
);


    // Close on Escape
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') this.#deactivate();
      },
      { signal: this.#abortController.signal }
    );

/** 
       this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
       signal: this.#abortController.signal,
    });
*/
    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    document.body.classList.remove('mega-menu-open');
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */


  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Toggle submenu on click.
   * For parent items (those with aria-controls), click toggles open/close and prevents navigation.
   */
  toggle = (event) => {
  if (!(event.target instanceof Element)) return;

  const item = findMenuItem(event.target);
  if (!item) return;

  const hasSubmenu = item.getAttribute('aria-controls');
  if (!hasSubmenu) return;

  event.preventDefault();
  event.stopPropagation(); // ✅ stop outside-click listener racing
  this.#debouncedDeactivate.cancel(); // ✅ stop blur-deactivate debounce racing

  const isOpen = item.getAttribute('aria-expanded') === 'true';
  if (isOpen) {
    this.#deactivate(item);
  } else {
    this.activate(event);
  }
};

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();



    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const slotted = event.target.closest?.('[slot]');
    const isDefaultSlot = !slotted || slotted.getAttribute('slot') === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

this.#state.activeItem = item;
this.ariaExpanded = 'true';
item.ariaExpanded = 'true';

let submenu = findSubmenu(item);
const hasRealSubmenu = !!submenu;

// Use overflow panel only for overflow slot behaviour
if (!submenu && !isDefaultSlot) submenu = this.overflowMenu;



// Mark submenu as active
if (submenu) submenu.dataset.active = '';

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    // if (this.overflowHovered) return;  // remove for click-to-open behaviour


    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    
    item.setAttribute('data-animating', '');

    

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // If click/focus was inside the link itself
  const direct = element.closest?.('[ref="menuitem"]');
  if (direct instanceof HTMLElement) return direct;

  // Fix typo + handle the "More" slot (button)
  if (element.closest?.('[slot="more"]')) {
    // Return first overflow menu item as the target
    const firstOverflow = element.closest('header-menu')?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return firstOverflow instanceof HTMLElement ? firstOverflow : null;
  }

  // Fallback: if we're on a wrapper element
  const inside = element.querySelector?.('[ref="menuitem"]');
  return inside instanceof HTMLElement ? inside : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
